import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { SocketContext } from '../SocketProvider';
import {
  judgeAndReturnType,
  formatData,
  addDelComment,
  addDelThread,
  editThreadComment,
  REQ_TYPE,
} from '../utils';

export const CommentCtx = React.createContext();

/**
 * Safe default when useComment is used outside CommentProvider (e.g. during
 * redirect/unmount after logout). Prevents "useComment is used outside of
 * the CommentProvider" and blank page. All consumers get safe no-op values.
 */
const SAFE_COMMENT_CONTEXT = {
  loading: false,
  threadData: [],
  fetchUser: async () => [],
  addComment: () => {},
  addThread: () => {},
  editComment: () => {},
  selfEmail: '',
  projectUsers: [],
  projectUsersWithInfo: [],
  urlTabMap: {},
  urlMap: {},
  checkUserOnSamePage: () => {},
  beforeJump: () => {},
  commentScrollRecord: {},
  history: {},
  dateFormatter: (x) => x,
};

export const useComment = () => {
  const context = useContext(CommentCtx);

  if (context === undefined) {
    return SAFE_COMMENT_CONTEXT;
  }

  return context;
};

const CommentProvider = ({
  fetchProjectUser,
  app,
  name,
  module,
  email,
  children,
  projectId,
  accessibleModules,
  urlTabMap,
  urlMap,
  checkUserOnSamePage,
  beforeJump,
  history,
  dateFormatter,
}) => {
  const { socket, connected } = useContext(SocketContext);

  const [loading, setLoading] = useState(false);
  const [threadData, setThreadData] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [projectUsersWithInfo, setProjectUsersWithInfo] = useState([]);
  const commentScrollRecord = useRef(null);
  const store = useRef([]);

  useEffect(() => {
    setThreadData([]);
    setProjectUsersWithInfo([]);
    setProjectUsers([]);

    store.current = [];

    console.log('commentState', { connected, projectId });
    if (connected && projectId) {
      socket.emit('comment:visitProject', { projectId });
      setLoading(true);
      socket.emit('comment:list', { projectId, app }, (data) => {
        console.log('comment:list', data);
        handleData(data);
        setLoading(false);
      });
    }
  }, [connected, projectId]);

  useEffect(() => {
    // socket.emit('comment:list', { projectId, app: 'ma' });

    socket.on('comment:projectUsers', (data) => {
      // setLoading(false);
      console.log('projectUsers:', data);
      setProjectUsers(data);
    });
    socket.on('comment:projectUsersWithInfo', (data) => {
      // setLoading(false);
      console.log('projectUsersWithInfo:', data);
      setProjectUsersWithInfo(data);
    });
    socket.on('comment:list', (data) => {
      // setLoading(false);

      console.log('add Comment:', data);
      handleData(data);
    });
    socket.on('comment:add', (data) => {
      // setLoading(false);

      console.log('add Comment:', data);
      handleData(data, 'add');
    });
    socket.on('comment:delete', (data) => {
      handleData(data, 'delete');
    });

    socket.on('comment:edit', (data) => {
      console.log('comment:edit event:', data);
      handleData(data, 'edit');
    });

    return () => {
      socket.emit('comment:leaveProject', { projectId });
    };
  }, []);

  useEffect(() => {
    if (history.location.pathname === '/projects/list') {
      socket.emit('comment:leaveProject', { projectId });
    }
  }, [history.location]);

  const handleAddDelThread = (commentData, type) =>
    addDelThread(store.current, commentData, type);

  const handleAddDelComment = (commentData, type) =>
    addDelComment(store.current, commentData, type);

  const handleEditThreadComment = (commentData, type) =>
    editThreadComment(store.current, commentData, type);

  const filterDisplayComment = useCallback(
    (thread) => {
      const { module, component, identifier } = thread;

      const moduleShouldShow =
        Object.keys(accessibleModules).includes(module) ||
        accessibleModules?._otherModule?.includes(identifier);

      if (!moduleShouldShow) {
        return false;
      }

      const allowAllComps = accessibleModules[module] === '*';

      if (allowAllComps) {
        return true;
      }

      const isPageLevel = thread.component === '' || thread.component == null;
      const componentShouldShow =
        isPageLevel ||
        accessibleModules[module].some(
          (comp) => component.indexOf(comp) !== -1 || comp === identifier,
        );

      return componentShouldShow;
    },
    [accessibleModules],
  );

  const handleData = (commentData, type) => {
    const {
      ADD_COMMENT,
      ADD_THREAD,
      FETCH_LIST,
      DEL_COMMENT,
      DEL_THREAD,
      EDIT_THREAD,
      EDIT_COMMENT,
    } = REQ_TYPE;
    let newData = [],
      reqType = FETCH_LIST;
    reqType = judgeAndReturnType(commentData, type);

    switch (reqType) {
      case ADD_COMMENT: {
        newData = handleAddDelComment(commentData, ADD_COMMENT);
        break;
      }
      case DEL_COMMENT: {
        newData = handleAddDelComment(commentData, DEL_COMMENT);
        break;
      }
      case ADD_THREAD: {
        newData = handleAddDelThread(commentData, ADD_THREAD);
        break;
      }
      case DEL_THREAD: {
        newData = handleAddDelThread(commentData, DEL_THREAD);
        break;
      }
      case EDIT_THREAD: {
        newData = handleEditThreadComment(commentData, EDIT_THREAD);

        break;
      }
      case EDIT_COMMENT: {
        newData = handleEditThreadComment(commentData, EDIT_COMMENT);
        break;
      }
      default:
        newData = formatData(commentData);
        break;
    }
    store.current = newData;
    setThreadData(newData);
  };

  const fetchUser = async (key) => {
    const userList = await fetchProjectUser(key);
    return userList;
    // setUserData(userList.slice(0, 10));
  };

  const newComment = (r) => Object.assign({ app, projectId, email, name }, r);

  const addComment = ({
    module,
    component,
    comment,
    channel,
    channelStatus,
    identifier = null,
    emails = [],
  }) => {
    setLoading(true);
    socket.emit(
      'comment:add',
      newComment({
        module,
        component,
        channel,
        comment,
        channelStatus,
        emails,
        identifier,
      }),
      (data) => {
        console.log('add response', data);
        setLoading(false);
      },
    );
  };

  const addThread = ({
    module,
    component,
    comment,
    emails,
    identifier = null,
  }) => {
    setLoading(true);
    socket.emit(
      'comment:add',
      newComment({ module, component, comment, emails, identifier }),
      (data) => {
        console.log('add response', data);
        setLoading(false);
      },
    );
  };

  const handleDelete = (data) => {
    const sameData = {
      channel: data.channel,
      projectId,
      email,
    };
    setLoading(true);
    if (data.id === data.channel) {
      socket.emit(
        'comment:delete',
        {
          type: 'channel',
          ...sameData,
        },
        (data) => {
          console.log('del thread response', data);
          setLoading(false);
        },
      );
    } else {
      socket.emit(
        'comment:delete',
        {
          type: 'comment',
          commentId: data.id,
          ...sameData,
        },
        (data) => {
          console.log('del comment response', data);
          setLoading(false);
        },
      );
    }
  };
  const handleEdit = (data, isChannelStatus) => {
    setLoading(true);
    const sameData = {
      projectId,
      email,
      emails: data.emails,
      identifier: data.identifier,
      name,
    };
    if (isChannelStatus) {
      socket.emit(
        'comment:edit',
        {
          ...sameData,
          channel: data.channel,
          type: 'channel',
          channelStatus: data.channelStatus,
          // email: data.email || sameData.email,
        },
        (data) => {
          console.log('edit response', data);
          setLoading(false);
        },
      );
      return;
    }
    socket.emit(
      'comment:edit',
      {
        ...sameData,
        channel: data.channel,
        commentId: data.id,
        type: 'comment',
        comment: data.comment,
      },
      (data) => {
        console.log('edit response', data);
        setLoading(false);
      },
    );
  };

  const editComment = (type, data, isChannelStatus) => {
    switch (type) {
      case 'DELETE':
        handleDelete(data);
        break;
      case 'EDIT':
        handleEdit(data, isChannelStatus);
        break;
      default:
        break;
    }

    // socket.emit('comment:list', { projectId, app });
  };

  const memoThreadData = useMemo(
    () => threadData.filter(filterDisplayComment),
    [filterDisplayComment, threadData],
  );

  return (
    <CommentCtx.Provider
      value={{
        loading: loading || !connected,
        threadData: memoThreadData,
        fetchUser,
        addComment,
        addThread,
        editComment,
        selfEmail: email,
        projectUsers,
        urlTabMap,
        urlMap,
        checkUserOnSamePage,
        beforeJump,
        commentScrollRecord,
        history,
        dateFormatter,
        projectUsersWithInfo,
      }}
    >
      {children}
    </CommentCtx.Provider>
  );
};

export default CommentProvider;
